import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  assignRoleFromSharing,
  fetchCurrentUser,
  fetchNeededAgreements,
  fetchUserProjectRoles,
  logoutUser,
  refreshUserToken,
} from "../store/usersSlice";
import {
  currentOrganizationSelector,
  devInfoSelector,
  userSelector,
} from "../store/selectors";
import { updateTariffes } from "../helpers/helpers";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.users.currentUser);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const loading = useSelector((state) => state.users.loading);
  const error = useSelector((state) => state.users.error);
  // const { isDev, devToken } = useSelector(devInfoSelector);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [areAllAgreementsAccepted, setAreAllAgreementsAccepted] =
    useState(false);
  const [sharedToken, setSharedToken] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);

  // On mount, if token exists, fetch current user
  useEffect(() => {
    const initializeAuth = async () => {
      console.log("Authenticating...");
      const decodedUrl = window.location.href.split("?access_token=");

      if (window.location.href.includes("/share/access/")) {
        setInitializing(false);
        return;
      }

      let token = localStorage.getItem("access_token");
      // if (isDev) {
      //   token = devToken;
      //   localStorage.setItem("access_token", devToken);
      // }

      if (!token && !!decodedUrl[1]) {
        token = decodedUrl[1];
        localStorage.setItem("access_token", token);
      }

      if (token) {
        dispatch(fetchCurrentUser()).then(async (res) => {
          if (res.error && res.payload.includes("401")) {
            dispatch(refreshUserToken()).then((result) => {
              if (result.error) {
                setInitializing(false);
                return;
              } else {
                login(result.payload.access_token);
              }
            });
          } else {
            const currentOrg = detectCurrentOrg(res.payload);
            dispatch(
              fetchUserProjectRoles({
                organizationId: currentOrg.id,
                userId: res.payload.userId,
              })
            );
            dispatch(assignRoleFromSharing({ contentVersionId: null })).then(
              (res) => {
                setIsAuthenticated(true);
                setInitializing(false);
              }
            );
            const agreementsRes = await dispatch(fetchNeededAgreements());

            if (!agreementsRes.error) {
              const needToAcceptSome = agreementsRes.payload.find(
                (agreement) => !agreement.accepted
              );

              if (needToAcceptSome) {
                localStorage.setItem("redirectUrl", `/legal-documents`);
              }
            }

            await updateTariffes(res.payload.organizations, dispatch);

            return true;
          }
        });
      } else {
        setInitializing(false);
      }
    };

    initializeAuth();
  }, [dispatch]);

  // Update isAuthenticated when currentUser changes
  useEffect(() => {
    setIsAuthenticated(!!currentUser);
  }, [currentUser]);

  // login function: set token and fetch current user
  const login = async (newToken) => {
    console.log("Logging in...");
    setInitializing(true);
    localStorage.setItem("access_token", newToken);
    dispatch(fetchCurrentUser()).then(async (res) => {
      if (res.error && res.payload.includes("401")) {
        console.log("Refreshing session...");
        dispatch(refreshUserToken()).then((result) => {
          if (result.error) {
            localStorage.removeItem("access_token");
            console.log("Requesting new log in...");
            //window.location.href = "https://go.7cut.video/api/v1/auth/login";
            window.location.href = `https://go.7cut.video/api/v1/auth/login?redirect=${window.location.origin}`;
          } else {
            const currentOrg = detectCurrentOrg(result.payload);
            dispatch(
              fetchUserProjectRoles({
                organizationId: currentOrg.id,
                userId: result.payload.userId,
              })
            );
          }
        });
      } else {
        dispatch(assignRoleFromSharing({ contentVersionId: null })).then(() => {
          setIsAuthenticated(true);
          setInitializing(false);
        });
        const currentOrg = detectCurrentOrg(res.payload);
        dispatch(
          fetchUserProjectRoles({
            organizationId: currentOrg.id,
            userId: res.payload.userId,
          })
        );

        const agreementsRes = await dispatch(fetchNeededAgreements());

        if (!agreementsRes.error) {
          const needToAcceptSome = agreementsRes.payload.find(
            (agreement) => !agreement.accepted
          );

          if (needToAcceptSome) {
            localStorage.setItem("redirectUrl", `/legal-documents`);
          }
        }

        await updateTariffes(res.payload.organizations, dispatch);
        return true;
      }
    });
    return true;
  };

  const detectCurrentOrg = (usr) => {
    const currentOrganizationId = localStorage.getItem("currentOrganizationId");
    if (currentOrganizationId) {
      const existingOrg = usr.organizations.find(
        (org) => org.id === +currentOrganizationId
      );
      if (existingOrg) {
        return existingOrg;
      } else {
        return usr.organizations[0];
      }
    } else {
      return usr.organizations[0];
    }
  };

  const loginFromShared = async (newToken, resourceType, resourceId) => {
    console.log("Shared logging in...");
    setInitializing(true);
    localStorage.setItem("access_token", newToken);

    const userRes = await dispatch(fetchCurrentUser());

    if (userRes.error && userRes.payload.includes("401")) {
      const refreshRes = await dispatch(refreshUserToken());
      if (refreshRes.error) {
        localStorage.removeItem("access_token");
        return false;
      } else {
        return true;
      }
    } else {
      const currentOrg = detectCurrentOrg(userRes.payload);
      dispatch(
        fetchUserProjectRoles({
          organizationId: currentOrg.id,
          userId: userRes.payload.userId,
        })
      );
    }

    let assignRes;

    if (resourceType === "VIDEO") {
      assignRes = await dispatch(
        assignRoleFromSharing({ contentVersionId: resourceId })
      );
    } else {
      assignRes = await dispatch(
        assignRoleFromSharing({ contentVersionId: null })
      );
    }

    const agreementsRes = await dispatch(fetchNeededAgreements());

    if (!agreementsRes.error) {
      const needToAcceptSome = agreementsRes.payload.find(
        (agreement) => !agreement.accepted
      );

      if (needToAcceptSome) {
        localStorage.setItem("redirectUrl", `/legal-documents`);
      }
    }

    setIsAuthenticated(true);
    setInitializing(false);
    return true;
  };

  const onChangeAreDocumentsAccepted = (value) => {
    setAreAllAgreementsAccepted(value);
  };

  const logout = () => {
    const clearLocalSession = () => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("currentOrganizationId");
      localStorage.removeItem("currentOrganizationOwnerId");
      localStorage.removeItem("redirectUrl");
      localStorage.removeItem("saved_emails");
    };

    dispatch(logoutUser()).then((res) => {
      if (!res.error) {
        clearLocalSession();
        window.location.href = window.location.origin + "/login";
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        sharedToken,
        setSharedToken,
        loginFromShared,
        currentUser,
        loading,
        error,
        initializing,
        areAllAgreementsAccepted,
        onChangeAreDocumentsAccepted,
        showReleaseNotes,
        setShowReleaseNotes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
